import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PrivacyNotice, ConsentBoxes } from "@/components/PrivacyNotice";
import { age } from "@/components/PrivacyGate";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import Seo from "@/components/Seo";
import { GraduationCap, Briefcase, Users, Loader2, ArrowRight } from "lucide-react";

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(new URLSearchParams(window.location.search).get("ref") ? "register" : "login");
  const [role, setRole] = useState("student");
  const [form, setForm] = useState({ name: "", email: "", password: "", dob: "", guardian_email: "" });
  const [accept, setAccept] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const underage = mode === "register" && form.dob && age(form.dob) < 18;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const referralCode = new URLSearchParams(window.location.search).get("ref") || "";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let u;
      if (mode === "login") {
        u = await login(form.email, form.password);
      } else {
        if (!accept) throw new Error("You cannot create an account without accepting the required Privacy Notice and Terms.");
        u = await register({ ...form, role, referral_code: referralCode || undefined, accept_required: accept,
          guardian_email: underage ? form.guardian_email : undefined,
          consents: underage ? {} : { marketing_email: marketing, marketing_whatsapp: marketing, marketing_sms: marketing } });
      }
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      if (u.role === "student" && !u.assessment_completed) navigate("/assessment");
      else navigate("/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white flex">
      <Seo title="Sign in or create your account" path="/auth"
        description="Sign in or create your free EntranceZone AI account to start your adaptive career assessment, predictors and mentorship." />
      {/* Left visual */}
      <div className="hidden lg:flex lg:w-1/2 relative radial-fade items-center justify-center p-12">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="relative max-w-md">
          <Logo size="lg" />
          <h2 className="mt-10 font-display text-4xl font-extrabold leading-tight tracking-tight">
            Decisions that shape your life deserve <span className="text-cyan-400">real intelligence.</span>
          </h2>
          <p className="mt-5 text-slate-400 leading-relaxed">
            AI assessment, rank & college predictors, and verified human mentors — built for Indian students.
          </p>
          <div className="mt-10 rounded-2xl overflow-hidden border border-white/10 glow-cyan">
            <img src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
              alt="Counselling session" className="w-full h-56 object-cover" />
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>

          <div className="flex gap-2 rounded-full bg-white/5 p-1 mb-8 w-fit">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${
                  mode === m ? "bg-cyan-400 text-[#041016]" : "text-slate-400 hover:text-white"
                }`}>
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight">
            {mode === "login" ? "Welcome back" : "Start your journey"}
          </h1>
          <p className="mt-2 text-slate-400 text-sm">
            {mode === "login" ? "Sign in to continue your career journey." : "Create an account in seconds."}
          </p>

          {referralCode && mode === "register" && (
            <div data-testid="referral-banner" className="mt-4 rounded-xl border border-emerald-400/25 bg-emerald-400/5 px-4 py-2.5 text-xs text-emerald-300">
              🎁 You were invited! Complete your assessment and your friend earns bonus Premium days.
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-300">I am a</label>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {[
                      { v: "student", label: "Student", icon: GraduationCap },
                      { v: "counsellor", label: "Counsellor", icon: Briefcase },
                      { v: "parent", label: "Parent", icon: Users },
                    ].map((r) => {
                      const Icon = r.icon;
                      return (
                        <button key={r.v} type="button" data-testid={`role-${r.v}-btn`} onClick={() => setRole(r.v)}
                          className={`flex flex-col items-center gap-1 rounded-xl border px-3 py-3 text-xs font-medium transition-all ${
                            role === r.v ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-slate-400 hover:border-white/20"
                          }`}>
                          <Icon className="h-4 w-4" /> {r.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Field label="Full name" testid="name-input" value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })} placeholder="Aarav Sharma" required />
              </>
            )}
            <Field label="Email" testid="email-input" type="email" value={form.email}
              onChange={(v) => setForm({ ...form, email: v })} placeholder="you@example.com" required />
            <Field label="Password" testid="password-input" type="password" value={form.password}
              onChange={(v) => setForm({ ...form, password: v })} placeholder="••••••••" required />
            {mode === "register" && (
              <>
                <Field label="Date of birth" testid="dob-input" type="date" value={form.dob}
                  onChange={(v) => setForm({ ...form, dob: v })} required />
                {underage && (
                  <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-3">
                    <Field label="Parent / guardian email (required under 18)" testid="guardian-email-input" type="email" value={form.guardian_email}
                      onChange={(v) => setForm({ ...form, guardian_email: v })} placeholder="parent@example.com" required />
                    <p className="mt-2 text-xs text-amber-200/80">We'll email your parent/guardian a code to confirm consent before the AI assessment and coach are enabled. No marketing or tracking is ever used for under-18 accounts.</p>
                  </div>
                )}
                <PrivacyNotice />
                <ConsentBoxes accept={accept} setAccept={setAccept} marketing={marketing} setMarketing={setMarketing} showMarketing={!underage} />
              </>
            )}

            {error && <p data-testid="auth-error" className="text-sm text-rose-400">{error}</p>}

            <button data-testid="auth-submit-btn" type="submit" disabled={loading || (mode === "register" && !accept)}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-400 to-cyan-500 px-6 py-3.5 text-base font-bold text-[#041016] transition-transform hover:scale-[1.01] disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="h-5 w-5" /></>}
            </button>
          </form>

          <div className="mt-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-slate-500">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button data-testid="google-login-btn" type="button" onClick={loginWithGoogle}
            className="mt-5 flex w-full items-center justify-center gap-3 rounded-xl border border-white/12 bg-white/[0.03] px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-white/[0.06]">
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.35 14.97.25 12 .25A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75z"/></svg>
            Continue with Google
          </button>

          <p className="mt-8 text-center text-xs text-slate-500">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button type="button" data-testid="auth-switch-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-semibold text-cyan-400 hover:text-cyan-300">
              {mode === "login" ? "Create a free account" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

const Field = ({ label, testid, type = "text", value, onChange, placeholder, required }) => (
  <div>
    <label className="text-sm font-medium text-slate-300">{label}</label>
    <input data-testid={testid} type={type} value={value} required={required} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-all focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20" />
  </div>
);
